import { destroy, types } from "mobx-state-tree";
import { IErrorModel } from "../../components/common/error/ErrorModel";
import i18n from "../../i18n";
import { IInput } from "../../utils/common-interface";
import customtypes from "../../utils/mobx-types-helper";
import { CurrencyModel, SelectListModel } from "../share";

export const TotalLoanByYearModel = types
    .model("TotalLoanByYearModel", {
        description: customtypes.optional(types.string, ""),
        year: customtypes.optional(types.number, 0),
        grandTotal: customtypes.optional(types.number, 0),
        localLoan: customtypes.optional(types.number, 0),
        foreignLoan: customtypes.optional(types.number, 0),
        otherLoan: customtypes.optional(types.number, 0),
        yearDescription: customtypes.optional(types.string, ""),
    })
    .views((self: any) => ({
        get grandTotalAsMillion() {
            return self.grandTotal / 1000000;
        },
        get localLoanAsMillion() {
            return self.localLoan / 1000000;
        },
        get foreignLoanAsMillion() {
            return self.foreignLoan / 1000000;
        },
        get otherLoanAsMillion() {
            return self.otherLoan / 1000000;
        },
    }))
    .actions((self: any) => ({
        setField: (id: number, code: string, value: string) => {
            //
        },
    }));
export type ITotalLoanByYearModel = typeof TotalLoanByYearModel.Type;

export const LoanTypeModel = types
    .model("LoanTypeModel", {
        description: customtypes.optional(types.string, ""),
        selectedType: customtypes.optional(types.string, ""),
        loanAmount: customtypes.optional(types.number, 0),
        currency: customtypes.optional(types.string, ""),
    })
    .views((self: any) => ({
        get loanAmountAsMillion() {
            return self.loanAmount / 1000000;
        },
        get selectedTypeText() {
            switch (self.selectedType) {
                case "D":
                    return i18n.t("share.LoanModel.directLoan");
                case "G":
                    return i18n.t("share.LoanModel.GKLoan");
                case "MF":
                    return i18n.t("share.LoanModel.MFLoan");
                case "FL":
                    return i18n.t("share.LoanModel.FLLoan");
                case "NG":
                    return i18n.t("share.LoanModel.NGLoan");
                default:
                    return "-";
            }
        },
        get valueList() { // ใช้กับ SummaryCardType2
            return {
                label: self.selectedTypeText,
                value: self.loanAmountAsMillion,
            };
        },
    }))
    .actions((self: any) => ({
        setField: (id: number, code: string, value: string) => {
            //
        },
    }));
export type ILoanTypeModel = typeof LoanTypeModel.Type;

export const LoanSourcesModel = types
    .model("LoanSourcesModel", {
        loanSourceID: types.maybeNull(types.number),
        sourceType: customtypes.optional(types.string, ""),
        loanAmount: customtypes.optional(types.number, 0),
        currency: customtypes.optional(types.string, ""),
        thbAmount: customtypes.optional(types.number, 0),
        sourceLoan: types.optional(SelectListModel, {}),
        jan: customtypes.optional(types.number, 0),
        feb: customtypes.optional(types.number, 0),
        mar: customtypes.optional(types.number, 0),
        apr: customtypes.optional(types.number, 0),
        may: customtypes.optional(types.number, 0),
        jun: customtypes.optional(types.number, 0),
        jul: customtypes.optional(types.number, 0),
        aug: customtypes.optional(types.number, 0),
        sep: customtypes.optional(types.number, 0),
        oct: customtypes.optional(types.number, 0),
        nov: customtypes.optional(types.number, 0),
        dec: customtypes.optional(types.number, 0),
        meetingResolutionAmount: customtypes.optional(types.number, 0),
    })
    .views((self: any) => ({
        get loanAmountAsMillionBaht() {
            return self.loanAmount / 1000000;
        },
        get thbAmountAsMillionBaht() {
            return self.thbAmount / 1000000;
        },
        get sourceTypeText() {
            switch (self.sourceType) {
                case "A":
                    return i18n.t("share.LoanModel.total");
                case "L":
                    return i18n.t("share.LoanModel.local");
                case "F":
                    return i18n.t("share.LoanModel.foreign");
                case "O":
                    return i18n.t("share.LoanModel.other");
                default:
                    return "-";
            }
        },
    }))
    .actions((self: any) => ({
        setCurrencyDropdown: (value: string) => {
            self.currency = value;
        },
        setField: ({ fieldname, value }: IInput) => {
            self[fieldname] = value;
        },
    }));
export type ILoanSourcesModel = typeof LoanSourcesModel.Type;

export const LoanTypePlanModel = types
    .model("LoanTypePlanModel", {
        description: customtypes.optional(types.string, ""),
        loanType: types.optional(LoanTypeModel, {}),
        loanSourcePlans: types.optional(types.array(LoanSourcesModel), []),
    })
    .views((self: any) => ({
        get localLoanPlanAmount() { // Local มีตัวเดียวเลย Return number
            const localLoanPlan: ILoanSourcesModel = self.loanSourcePlans.find((loanSource: ILoanSourcesModel) =>
                loanSource.sourceType === "L" && loanSource.currency === "THB",
            );
            return localLoanPlan ? localLoanPlan.loanAmount : 0;
        },
        get foreignLoanPlans() { // Foregin มีหลายตัว Return เป็น List ออกไป
            return self.loanSourcePlans.filter((loanSource: ILoanSourcesModel) =>
                loanSource.sourceType === "F",
            );
        },
        get otherLoanPlanAmount() {// Other มีตัวเดียวเลย Return number
            const otherLoanPlan: ILoanSourcesModel = self.loanSourcePlans.find((loanSource: ILoanSourcesModel) =>
                loanSource.sourceType === "O" && loanSource.currency === "THB",
            );
            return otherLoanPlan ? otherLoanPlan.loanAmount : 0;
        },
        get loanTypePlanJSON() {
            return self.toJSON();
        },
    }))
    .actions((self: any) => ({
        onAddForeignLoanSource: () => {
            self.loanSourcePlans.push({ sourceType: "F", currency: "USD" });
        },
        onRemoveForeignLoanSource: (loanSource: ILoanSourcesModel) => {
            destroy(loanSource);
        },
        setAllField: (data: any) => {
            Object.keys(data).forEach((key) => {
                try {
                    self[key] = data[key];
                } catch (e) {
                    console.log("exception on mapping key:", key);
                }
            });
        },

        setField: (id: number, code: string, value: string) => {
            //
        },
    }));
export type ILoanTypePlanModel = typeof LoanTypePlanModel.Type;

export const LoanPeriodsModel = types
    .model("LoanPeriodsModel", {
        periodType: customtypes.optional(types.string, "Y"), // y: Year
        periodValue: customtypes.optional(types.number, 0),
        periodDescription: customtypes.optional(types.string, ""),
        loanSources: types.optional(types.array(LoanSourcesModel), [
            { sourceType: "L", currency: "THB", sourceLoan: {} }, // default THAI BAHT loan source
        ]),
        selectLoanSource: types.optional(LoanSourcesModel, {}),
    })
    .views((self: any) => ({
        get domesticLoanSource(): ILoanSourcesModel {
            return self.loanSources.find((loanSource: ILoanSourcesModel, index: number) =>
                loanSource.sourceType === "L",
            );
        },
        get abroadLoanSource() {
            return self.loanSources.filter((loanSource: ILoanSourcesModel, index: number) =>
                loanSource.sourceType === "F",
            );
        },
        get loanPeriodsJSON() {
            return self.toJSON();
        },
    }))
    .actions((self: any) => ({
        onAddAbroadLoanSource: () => {
            self.loanSources.push({ sourceType: "F", currency: "USD" });
        },
        onAddLoanSource: (err : IErrorModel, t : any) => {
            var i : number = 0;
            self.loanSources.forEach((o : ILoanSourcesModel, index : number) => {
                 

                if (o.sourceLoan!.id == self.selectLoanSource!.sourceLoan!.id && o.currency == self.selectLoanSource!.currency)
                    { 
                        try {
                        throw {
                          message: t("modal.M1335YFormInputByItemModal.errorDuplicateAddSourceLoan"),
                        }
                        } catch (e) {
                        err.setField({ fieldname: "tigger", value: true });
                        err.setField({ fieldname: "message", value: e.message });
                      }    
                    }                
                 else {
                    if (o.sourceLoan.code > self.selectLoanSource!.sourceLoan!.code){
                        return;
                    }
                    console.log(i);
                    i++;
                   
                }

            });
            console.log("finish " + i)
            self.loanSources.splice(i,0,{ sourceType: "L", currency:  self.selectLoanSource.currency ,
            sourceLoan: {
                id: self.selectLoanSource.sourceLoan.id,
                description: self.selectLoanSource.sourceLoan.description,
                code: self.selectLoanSource.sourceLoan.code
                }
            });

        },
 

        onRemoveLoanSource: (index: number) => {
            self.loanSources.splice(index + 1, 1); // + 1 เพราะ loanSources มี domestic ตอนสร้างอยู่หนึ่งตัวอยู่แล้ว

        },
        onRemoveLoanSourceNew: (index: number) => {

            self.loanSources.splice(index, 1); // kenghot

        },   
        onSelectLoanSource: (data:any) => {
             
            self.selectLoanSource.sourceLoan = { id: data.value, code: data.code, description: data.text }
            
        },     
        onSelectCurrency: (data:any) => {
            
            self.selectLoanSource.currency = data.value
             
        },
        setField: ({ fieldname, value }: IInput) => {
            self[fieldname] = value;
        },
    }));
export type ILoanPeriodsModel = typeof LoanPeriodsModel.Type;

export const LoanSourcePlanModel = types
    .model("LoanSourcePlanModel", {
        description: customtypes.optional(types.string, ""),
        loanSource: types.optional(LoanSourcesModel, {}),
        loanTypePlans: types.optional(types.array(LoanTypeModel), []),
    })
    .views((self: any) => ({
        //
    }))
    .actions((self: any) => ({
        setField: (id: number, code: string, value: string) => {
            //
        },
    }));
export type ILoanSourcePlanModel = typeof LoanSourcePlanModel.Type;

export const LoanProcessPlanDetailModel = types
    .model("LoanProcessPlanDetailModel", {
        year: customtypes.optional(types.number, 0),
        month: customtypes.optional(types.number, 1), // เดือน 1-12 (1 = มกราคม)
        signedLoan: customtypes.optional(types.number, 0),
        disburseLoan: customtypes.optional(types.number, 0),
    })
    .views((self: any) => ({
        //
    }))
    .actions((self: any) => ({
        setSignedLoan: (value: number) => {
            self.signedLoan = value;
        },
        setDisburseLoan: (value: number) => {
            self.disburseLoan = value;
        },
    }));
export type ILoanProcessPlanDetailModel = typeof LoanProcessPlanDetailModel.Type;

export const LoanProcessPlanModel = types
    .model("LoanProcessPlanModel", {
        description: customtypes.optional(types.string, ""),
        loanSource: types.optional(LoanSourcesModel, {}),
        loanProcessPlanDetails: types.optional(types.array(LoanProcessPlanDetailModel), []),
        signedLoanSumAmount: customtypes.optional(types.number, 0),
        disburseLoanSumAmount: customtypes.optional(types.number, 0),
    })
    .views((self: any) => ({
        get loanProcessPlanDetailsStartOnOctober() {
            return (year: any) => [
                self.loanProcessPlanDetails.find((plan: ILoanProcessPlanDetailModel) =>
                    plan.month === 7 && plan.year ===year-1,
                ),
                self.loanProcessPlanDetails.find((plan: ILoanProcessPlanDetailModel) =>
                    plan.month === 8 && plan.year ===year-1,
                ),
                self.loanProcessPlanDetails.find((plan: ILoanProcessPlanDetailModel) =>
                    plan.month === 9 && plan.year ===year-1,
                ),
                self.loanProcessPlanDetails.find((plan: ILoanProcessPlanDetailModel) =>
                    plan.month === 10 && plan.year ===year,
                ),
                self.loanProcessPlanDetails.find((plan: ILoanProcessPlanDetailModel) =>
                    plan.month === 11 && plan.year ===year,
                ),
                self.loanProcessPlanDetails.find((plan: ILoanProcessPlanDetailModel) =>
                    plan.month === 12 && plan.year ===year,
                ),
                self.loanProcessPlanDetails.find((plan: ILoanProcessPlanDetailModel) =>
                    plan.month === 1 && plan.year ===year,
                ),
                self.loanProcessPlanDetails.find((plan: ILoanProcessPlanDetailModel) =>
                    plan.month === 2 && plan.year ===year,
                ),
                self.loanProcessPlanDetails.find((plan: ILoanProcessPlanDetailModel) =>
                    plan.month === 3 && plan.year ===year,
                ),
                self.loanProcessPlanDetails.find((plan: ILoanProcessPlanDetailModel) =>
                    plan.month === 4 && plan.year ===year,
                ),
                self.loanProcessPlanDetails.find((plan: ILoanProcessPlanDetailModel) =>
                    plan.month === 5 && plan.year ===year,
                ),
                self.loanProcessPlanDetails.find((plan: ILoanProcessPlanDetailModel) =>
                    plan.month === 6 && plan.year ===year,
                ),
                self.loanProcessPlanDetails.find((plan: ILoanProcessPlanDetailModel) =>
                    plan.month === 7 && plan.year ===year,
                ),
                self.loanProcessPlanDetails.find((plan: ILoanProcessPlanDetailModel) =>
                    plan.month === 8 && plan.year ===year,
                ),
                self.loanProcessPlanDetails.find((plan: ILoanProcessPlanDetailModel) =>
                    plan.month === 9 && plan.year ===year,
                ),
                self.loanProcessPlanDetails.find((plan: ILoanProcessPlanDetailModel) =>
                    plan.month === 10 && plan.year ===year+1,
                ),
                self.loanProcessPlanDetails.find((plan: ILoanProcessPlanDetailModel) =>
                    plan.month === 11 && plan.year ===year+1,
                ),
                self.loanProcessPlanDetails.find((plan: ILoanProcessPlanDetailModel) =>
                    plan.month === 12 && plan.year ===year+1,
                ),
            ];
        },
        get totalSignedLoan() {
            return self.loanProcessPlanDetails.reduce((sum: number, loanProcessDetail: ILoanProcessPlanDetailModel) =>
                sum + loanProcessDetail.signedLoan, 0);
        },
        get totalDisburseLoan() {
            return self.loanProcessPlanDetails.reduce((sum: number, loanProcessDetail: ILoanProcessPlanDetailModel) =>
                sum + loanProcessDetail.disburseLoan, 0);
        },
        get loanProcessPlanJSON() {
            return self.toJSON();
        },
    }))
    .actions((self: any) => ({
        getLoanProcessPlanDetailsFromMonth: (monthIndex: number) => {
            return self.loanProcessPlanDetails.find((loanProcessDetail: ILoanProcessPlanDetailModel) =>
                loanProcessDetail.month === monthIndex,
            );
        },
        getLoanProcessPlanDetailsFromMonthAndYear: (monthIndex: number,yearIndex: number) => {
            return self.loanProcessPlanDetails.find((loanProcessDetail: ILoanProcessPlanDetailModel) =>
                loanProcessDetail.month === monthIndex && loanProcessDetail.year === yearIndex,
            );
        },
        addLoanProcessPlanDetails: (month: number) => {
            self.loanProcessPlanDetails.push(LoanProcessPlanDetailModel.create({ month}));
        },
        addMonthYearLoanProcessPlanDetails: (month: number,year: number) => {
            self.loanProcessPlanDetails.push(LoanProcessPlanDetailModel.create({ month,year}));
        },
        setField: ({ fieldname, value }: IInput) => {
            self[fieldname] = value;
        },
        setAllField: (data: any) => {
            Object.keys(data).forEach((key) => {
                try {
                    self[key] = data[key];
                } catch (e) {
                    console.log("exception on mapping key:", key);
                }
            });
        },
    }));
export type ILoanProcessPlanModel = typeof LoanProcessPlanModel.Type;
